/**
 * uiStore — theme, per-product layout state, panel sizes. Persisted to
 * localStorage (`loremap.ui`); the two-pane layout is remembered PER
 * PRODUCT (`perProject`, keyed by the product id since the map retired;
 * entries keyed by an old repo id orphan and defaults apply).
 *
 * v3 layout model (2026-08-23 simplification): the Read/Split/Map viewport
 * switcher is GONE — redundant with collapse. Exactly two affordances
 * remain: the collapse/expand toggle and the drag handle. Persisted
 * focus-map states migrate to `collapsed: true` (persist v3).
 *
 *  - `side`: which side the transcript column sits on. Default RIGHT;
 *    pre-v2 persisted state migrates to 'right' on hydrate (persist v2).
 *  - `transcriptWidth`: 320 up to min(1000px, 65% of the window), default
 *    460 — clamped on every write AND again at render (SessionPage), so a
 *    window resize never leaves the chat overgrown.
 *  - `collapsed`: the transcript collapsed to zero width via the collapse
 *    tab in the pane's rail, which is also the way back.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { Ulid } from '../api/types';

export type Theme = 'dark' | 'light';

export type TranscriptSide = 'left' | 'right';

export const TRANSCRIPT_MIN_WIDTH = 320;
/** Hard ceiling; the effective ceiling is min(this, 65% of the window). */
export const TRANSCRIPT_MAX_WIDTH = 1000;
export const TRANSCRIPT_MAX_FRACTION = 0.65;
export const TRANSCRIPT_DEFAULT_WIDTH = 460;
/** Below this width the transcript uses the compact type scale. */
export const TRANSCRIPT_COMPACT_WIDTH = 380;

/**
 * Clamp a transcript width into [320, min(1000, 65% of the window)].
 * `viewportWidth` overrides `window.innerWidth` (tests, render-time clamp).
 */
export function clampTranscriptWidth(width: number, viewportWidth?: number): number {
  const vw =
    viewportWidth ??
    (typeof window !== 'undefined' ? window.innerWidth : Number.POSITIVE_INFINITY);
  const ceiling = Math.max(
    TRANSCRIPT_MIN_WIDTH,
    Math.min(TRANSCRIPT_MAX_WIDTH, Math.floor(vw * TRANSCRIPT_MAX_FRACTION)),
  );
  return Math.min(ceiling, Math.max(TRANSCRIPT_MIN_WIDTH, width));
}

export interface ProjectUiState {
  /** which side of the canvas the transcript sits on (right default) */
  side: TranscriptSide;
  /** transcript column width, px */
  transcriptWidth: number;
  /** transcript collapsed to zero width via the collapse tab */
  collapsed: boolean;
}

export const defaultProjectUi: ProjectUiState = {
  side: 'right',
  transcriptWidth: TRANSCRIPT_DEFAULT_WIDTH,
  collapsed: false,
};

export interface UiStoreState {
  theme: Theme;
  /**
   * The repo the mounted product page resolved to (SessionPage: the
   * remembered repo when it still belongs to the product, else its first),
   * for shell surfaces outside the route tree. Sessions have no repo on the
   * path (`/products/:pid/chats/:sid`); this is how they remember one.
   */
  activeProjectId: Ulid | null;
  perProject: Record<string, ProjectUiState>;
  paletteOpen: boolean;
  /**
   * The app rail collapsed to its icon-only width by the user's toggle.
   * Persisted — a collapsed rail stays collapsed across visits.
   */
  railCollapsed: boolean;
  /**
   * The docked panels' height in px (a part's detail, the infrastructure
   * part's detail): one remembered number, dragged from the grip on the
   * dock's top edge (2026-09-03). Persisted.
   */
  partPanelHeight: number;

  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setActiveProject: (id: Ulid | null) => void;
  /** Read helper — always returns a full state (defaults fill missing keys). */
  projectUi: (projectId: Ulid) => ProjectUiState;
  setProjectUi: (projectId: Ulid, patch: Partial<ProjectUiState>) => void;
  setPaletteOpen: (open: boolean) => void;
  toggleRail: () => void;
  setPartPanelHeight: (px: number) => void;
  /** Account page: forget every remembered per-repo layout (back to defaults). */
  resetLayouts: () => void;
}

/**
 * What the browser's own chrome (Safari's tab bar, Android's status bar)
 * paints behind the app: each theme's canvas colour. tokens.css
 * `--bg-canvas` is the source; these are its 8-bit sRGB renderings
 * (oklch(0.288 0.022 277.5) is #282a36, oklch(0.958 0.017 92) is #f5f1e5),
 * written out because index.html sets the tag inline before any stylesheet
 * has loaded, and the two must match. index.html seeds the same pair.
 */
export const THEME_COLOR: Record<Theme, string> = { dark: '#282a36', light: '#f5f1e5' };

/** The docked panels' height: floor, ceiling, and where it starts. */
export const PANEL_MIN_HEIGHT = 160;
export const PANEL_MAX_HEIGHT = 720;
export const PANEL_DEFAULT_HEIGHT = 320;

export function clampPanelHeight(px: number): number {
  if (!Number.isFinite(px)) return PANEL_DEFAULT_HEIGHT;
  return Math.min(PANEL_MAX_HEIGHT, Math.max(PANEL_MIN_HEIGHT, Math.round(px)));
}

export function applyThemeAttribute(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', THEME_COLOR[theme]);
  }
}

/**
 * The theme a FIRST visit starts in: whatever the OS asks for. A stored
 * choice always wins (persist rehydrates over this synchronously, and
 * index.html runs the same lookup inline before first paint so there is no
 * flash of the wrong palette). Environments without matchMedia (jsdom, old
 * embedders) fall back to dark, the app's native palette.
 */
export function systemTheme(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/** Stored entries may predate new keys — always merge over the defaults. */
function fullProjectUi(stored: Partial<ProjectUiState> | undefined): ProjectUiState {
  return { ...defaultProjectUi, ...(stored ?? {}) };
}

/** A pre-v3 persisted entry may still carry the retired viewport key. */
type StoredEntry = Partial<ProjectUiState> & { viewport?: string };

/**
 * Persist v1 → v2 migration (DESIGN2 §1.1): every previously-seen repo moves
 * to `side: 'right'` once (the accidental v1 left default is not preserved);
 * widths clamp into range.
 */
export function migratePersistedUi(persisted: unknown): unknown {
  if (typeof persisted !== 'object' || persisted === null) return persisted;
  const state = persisted as { perProject?: Record<string, StoredEntry> };
  if (state.perProject === undefined) return persisted;
  const perProject: Record<string, StoredEntry> = {};
  for (const [id, entry] of Object.entries(state.perProject)) {
    perProject[id] = {
      ...entry,
      side: 'right',
      transcriptWidth: clampTranscriptWidth(
        typeof entry.transcriptWidth === 'number'
          ? entry.transcriptWidth
          : TRANSCRIPT_DEFAULT_WIDTH,
      ),
    };
  }
  return { ...state, perProject };
}

/**
 * Persist v3 → v4: the map retired (DECISIONS #62), and with it the detail
 * panel. Its keys are dropped; layouts keyed by a repo id simply orphan (the
 * key is a product id now) and defaults apply.
 */
export function migratePanelKeysOut(persisted: unknown): unknown {
  if (typeof persisted !== 'object' || persisted === null) return persisted;
  const state = persisted as {
    perProject?: Record<
      string,
      StoredEntry & { detailPanelWidth?: unknown; detailPanelOpen?: unknown; panelSide?: unknown }
    >;
  };
  if (state.perProject === undefined) return persisted;
  const perProject: Record<string, ProjectUiState> = {};
  for (const [id, entry] of Object.entries(state.perProject)) {
    const { detailPanelWidth, detailPanelOpen, panelSide, ...rest } = entry;
    void detailPanelWidth;
    void detailPanelOpen;
    void panelSide;
    perProject[id] = fullProjectUi(rest);
  }
  return { ...state, perProject };
}

/**
 * Persist v2 → v3: the viewport model is retired. A persisted `focus-map`
 * becomes `collapsed: true` (same user-visible state); the key is dropped.
 */
export function migrateViewportToCollapse(persisted: unknown): unknown {
  if (typeof persisted !== 'object' || persisted === null) return persisted;
  const state = persisted as { perProject?: Record<string, StoredEntry> };
  if (state.perProject === undefined) return persisted;
  const perProject: Record<string, ProjectUiState> = {};
  for (const [id, entry] of Object.entries(state.perProject)) {
    const { viewport, ...rest } = entry;
    perProject[id] = {
      ...fullProjectUi(rest),
      collapsed: rest.collapsed === true || viewport === 'focus-map',
    };
  }
  return { ...state, perProject };
}

export const useUiStore = create<UiStoreState>()(
  persist(
    (set, get) => ({
      theme: systemTheme(),
      activeProjectId: null,
      perProject: {},
      paletteOpen: false,
      railCollapsed: false,
      partPanelHeight: PANEL_DEFAULT_HEIGHT,

      setTheme(theme) {
        set({ theme });
        applyThemeAttribute(theme);
      },

      toggleTheme() {
        get().setTheme(get().theme === 'dark' ? 'light' : 'dark');
      },

      setActiveProject(id) {
        set({ activeProjectId: id });
      },

      projectUi(projectId) {
        return fullProjectUi(get().perProject[projectId]);
      },

      setProjectUi(projectId, patch) {
        const next = { ...patch };
        if (typeof next.transcriptWidth === 'number') {
          next.transcriptWidth = clampTranscriptWidth(next.transcriptWidth);
        }
        set((s) => ({
          perProject: {
            ...s.perProject,
            [projectId]: { ...fullProjectUi(s.perProject[projectId]), ...next },
          },
        }));
      },

      setPaletteOpen(open) {
        set({ paletteOpen: open });
      },

      toggleRail() {
        set((s) => ({ railCollapsed: !s.railCollapsed }));
      },

      setPartPanelHeight(px) {
        set({ partPanelHeight: clampPanelHeight(px) });
      },

      resetLayouts() {
        set({ perProject: {} });
      },
    }),
    {
      name: 'loremap.ui',
      version: 4,
      migrate: (persisted, version) => {
        let state = persisted;
        if (version < 2) state = migratePersistedUi(state);
        if (version < 3) state = migrateViewportToCollapse(state);
        if (version < 4) state = migratePanelKeysOut(state);
        return state as Pick<UiStoreState, 'theme' | 'perProject' | 'railCollapsed' | 'partPanelHeight'>;
      },
      partialize: (s) => ({
        theme: s.theme,
        perProject: s.perProject,
        railCollapsed: s.railCollapsed,
        partPanelHeight: s.partPanelHeight,
      }),
      onRehydrateStorage: () => (state) => {
        applyThemeAttribute(state?.theme ?? systemTheme());
      },
    },
  ),
);

/** Hook form of `projectUi` that subscribes to changes for one project. */
export function useProjectUi(projectId: Ulid): ProjectUiState {
  const stored = useUiStore((s) => s.perProject[projectId]);
  return fullProjectUi(stored);
}
